import { action } from '@ember/object';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import HotelSettingsMappingRoute from 'lumen/routes/hotel/settings/mapping';
import { MAPPING_URL } from 'lumen/utils/constants/pms-mapping-constants';
import PmsMappingValueBenchmark from 'lumen/utils/datamodels/mapping/pms-mapping-value-benchmark';

export default class HotelSettingsMappingBenchmarkRoute extends HotelSettingsMappingRoute {
  queryParams = {
    activeTab: {
      refreshModel: true,
    },
  };

  @service ajax;
  @service('benchmark-insight/beni-service') benchmarkInsightBeniService;
  @service('hotel-and-market') hotelAndMarketService;
  @service usageAnalytics;

  leaveWithoutSaving = false;

  fetchHierarchyTask = task({ restartable: true }, async (breakdown) => {
    const queryParams = {
      subscription_id: this.hotelAndMarketService.hotel.id,
      breakdown,
    };
    const hierarchyUrl = formatUrl(
      '/api/v3/benchmarkinsight/mapping/hierarchy',
      queryParams,
      this.ajax.host,
    );
    return this.ajax.request(hierarchyUrl);
  });

  fetchMappingTask = task({ restartable: true }, async (breakdown, url) => {
    const queryParams = {
      subscription_id: this.hotelAndMarketService.hotel.id,
      breakdown,
    };
    const mappingUrl = formatUrl(url, queryParams, this.ajax.host);

    return this.ajax.request(mappingUrl);
  });

  hierarchyTask = task(
    { restartable: true },
    async (breakdown, fetchFromApi = true) => {
      let response;

      if (fetchFromApi) {
        response = await this.fetchHierarchyTask.perform(breakdown);
      } else {
        response = this.fetchHierarchyTask.last.value;
      }
      return this.benchmarkInsightBeniService.createRootCategory(
        response.hierarchy,
        breakdown,
      );
    },
  );

  mappingTask = task(
    { restartable: true },
    async (breakdown, fetchFromApi = true) => {
      let response;

      if (fetchFromApi) {
        response = await this.fetchMappingTask.perform(
          breakdown,
          MAPPING_URL.BENCHMARK,
        );
      } else {
        response = this.fetchMappingTask.last.value;
      }

      await this.hierarchyTask.last; // Make sure the last hierarchy task has finished running
      const rootCategory = this.hierarchyTask.last.value;

      return {
        mapping: this.createPmsMappingValues(
          response.mapping,
          rootCategory,
          PmsMappingValueBenchmark,
        ),
        meta: {
          mappingCoverage: response.meta.mapping_coverage,
          mappingCoverageThreshold: response.meta.mapping_coverage_threshold,
        },
      };
    },
  );

  activate() {
    super.activate(...arguments);

    // Reset the "leave without saving" state on route activation so the willTransition hook will recheck the unsaved changes
    this.leaveWithoutSaving = false;
  }

  beforeModel() {
    if (!this.access.hasBenchmarkInsightMappingTab) {
      this.router.transitionTo('hotel.settings.index');
    }
  }

  createPmsMappingValues(mappingResponse, rootCategory, pmsMappingValueClass) {
    if (!mappingResponse) {
      return [];
    }

    // Create a data class for every row and set reference to the category.
    const rows = mappingResponse.map((pmsMappingValue) => {
      const row = pmsMappingValueClass.fromApiResponse(pmsMappingValue);
      row.updateCategoryRef(rootCategory);

      return row;
    });

    return rows;
  }

  model(params) {
    this.usageAnalytics.track('Mapping Starting', {
      eventTrigger: 'hotel-settings-mapping-benchmark',
      product: 'benchmark-insight',
    });

    const model = {};

    model.hierarchy = this.hierarchyTask;
    model.hierarchy.perform(params.activeTab);

    model.mapping = this.mappingTask;
    model.mapping.perform(params.activeTab);

    return model;
  }

  resetController(controller, isExiting) {
    // Reset disabled states when leaving the mapping route
    if (isExiting) {
      controller.isEditing = false;
    }
  }

  @action
  async willTransition(transition) {
    // Check if there are unsaved changes and trigger the confirmation alert in that case.
    // Ignore if the user previously confirmed to exit without saving to prevent an infinite loop.
    if (
      this.controller.hasUnsavedChanges &&
      (transition.from.queryParams.activeTab === undefined ||
        transition.from.queryParams?.activeTab !==
          transition.to.queryParams?.activeTab) &&
      !transition.isAborted &&
      !this.leaveWithoutSaving
    ) {
      // Always abort the transition, this is required because the alert is async and otherwise the transition will already be done
      // before the alert was shown.
      transition.abort();

      this.leaveWithoutSaving = await this.controller.showUnsavedChangesAlert();

      if (this.leaveWithoutSaving) {
        // Retry the aborted transition to continue
        transition.retry();
      }

      // Do not bubble willTransition as it's handled in this route
      return false;
    }

    // Bubble the willTransition event
    return true;
  }
}
